/**
 * Consistent formatting utilities for dates, currency, and status labels.
 * UK locale throughout: £1,250.00, 23 Mar 2026, etc.
 */

const GBP = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  minimumFractionDigits: 2,
});

const GBP_WHOLE = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/**
 * Format currency: £1,250.00
 */
export function formatCurrency(amount, { wholeNumbers = false } = {}) {
  if (amount == null || isNaN(amount)) return '—';
  return wholeNumbers ? GBP_WHOLE.format(amount) : GBP.format(amount);
}

/**
 * Format date: "23 Mar 2026"
 */
export function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr; // return as-is if unparseable
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Format date short: "23 Mar"
 */
export function formatDateShort(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/**
 * Format date with time: "23 Mar 2026, 14:30"
 */
export function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

/**
 * Check if a date is overdue (past today)
 */
export function isOverdue(dateStr) {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date();
}

/**
 * Check if a date is due within N days
 */
export function isDueSoon(dateStr, withinDays = 30) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const now = new Date();
  if (d < now) return false; // already overdue, not "due soon"
  const diffDays = (d - now) / (1000 * 60 * 60 * 24);
  return diffDays <= withinDays;
}

/**
 * Get deadline status: 'overdue' | 'due-soon' | 'on-track'
 */
export function getDeadlineStatus(dateStr) {
  if (!dateStr) return 'on-track';
  if (isOverdue(dateStr)) return 'overdue';
  if (isDueSoon(dateStr, 30)) return 'due-soon';
  return 'on-track';
}

/**
 * Days until/since a date. Negative = overdue.
 */
export function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const now = new Date();
  return Math.ceil((d - now) / (1000 * 60 * 60 * 24));
}
